/**
 * The form for an ollie-chat case: the chat (it must end with the user's message), the
 * chat type and the user's details, the rarer context as JSON, and what the reply must
 * and must not do, with the tools it must or must not call.
 */
import { useState } from 'react';
import { ConversationEditor, toConversation, toLines, type Line } from './ConversationEditor';
import { FieldLabel, ListField, fieldClass, isRecord, strings, toList, type ContentEditorProps } from './editorParts';

const TOOL_RULES = ['any', 'required', 'forbidden'] as const;
type ToolRule = (typeof TOOL_RULES)[number];

/** The input fields this form has no inputs for; edited as one JSON object. */
const CONTEXT_KEYS = ['ocdProfile', 'chatMemory', 'chatReference'];

interface Draft {
  lines: Line[];
  chatType: string;
  isPro: boolean;
  memberForDays: string;
  questDay: string;
  questDayCompleted: boolean;
  context: string;
  mustDo: string;
  mustNotDo: string;
  tools: Record<string, ToolRule>;
}

function toDraft(input: unknown, expect: unknown, toolNames: string[]): Draft {
  const source = isRecord(input) ? input : {};
  const user = isRecord(source.user) ? source.user : {};
  const expected = isRecord(expect) ? expect : {};
  const tools = isRecord(expected.tools) ? expected.tools : {};
  const context = Object.fromEntries(CONTEXT_KEYS.filter((key) => source[key] !== undefined).map((key) => [key, source[key]]));
  return {
    lines: toLines(input),
    chatType: typeof source.chatType === 'string' ? source.chatType : '',
    isPro: user.isPro !== false,
    memberForDays: typeof user.memberForDays === 'number' ? String(user.memberForDays) : '',
    questDay: typeof user.questDay === 'number' ? String(user.questDay) : '',
    questDayCompleted: user.questDayCompleted === true,
    context: Object.keys(context).length > 0 ? JSON.stringify(context, null, 2) : '',
    mustDo: strings(expected.mustDo).join('\n'),
    mustNotDo: strings(expected.mustNotDo).join('\n'),
    tools: Object.fromEntries(
      toolNames.map((name): [string, ToolRule] => [name, strings(tools.required).includes(name) ? 'required' : strings(tools.forbidden).includes(name) ? 'forbidden' : 'any']),
    ),
  };
}

const optionalNumber = (text: string): number | undefined => (text.trim() === '' ? undefined : Number(text));

/** The case content, or a problem that stops it being sent. */
function fromDraft(draft: Draft, toolNames: string[]): { input: unknown; expect: unknown } | { problem: string } {
  let context: Record<string, unknown> = {};
  if (draft.context.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(draft.context);
      if (!isRecord(parsed)) return { problem: 'The context must be a JSON object.' };
      context = parsed;
    } catch (caught) {
      return { problem: `Context: ${caught instanceof Error ? caught.message : 'not valid JSON.'}` };
    }
  }

  const memberForDays = optionalNumber(draft.memberForDays);
  const questDay = optionalNumber(draft.questDay);
  const required = toolNames.filter((name) => draft.tools[name] === 'required');
  const forbidden = toolNames.filter((name) => draft.tools[name] === 'forbidden');
  const mustNotDo = toList(draft.mustNotDo);

  return {
    input: {
      ...(draft.chatType && { chatType: draft.chatType }),
      conversation: toConversation(draft.lines),
      ...context,
      user: {
        isPro: draft.isPro,
        ...(memberForDays !== undefined && { memberForDays }),
        ...(questDay !== undefined && { questDay, questDayCompleted: draft.questDayCompleted }),
      },
    },
    expect: {
      mustDo: toList(draft.mustDo),
      ...(mustNotDo.length > 0 && { mustNotDo }),
      ...((required.length > 0 || forbidden.length > 0) && {
        tools: { ...(required.length > 0 && { required }), ...(forbidden.length > 0 && { forbidden }) },
      }),
    },
  };
}

const smallField = 'px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-900 disabled:bg-gray-50';

export const OllieChatContentEditor = ({ initial, options, disabled, onChange }: ContentEditorProps) => {
  // The chat prompt's tools, from the API (chatTools.ts), so the form never offers a name the API refuses.
  const toolNames = options.tools ?? [];
  const [draft, setDraft] = useState<Draft>(() => toDraft(initial?.input, initial?.expect, toolNames));
  const [problem, setProblem] = useState<string | null>(null);

  const update = (change: Partial<Draft>) => {
    const next = { ...draft, ...change };
    setDraft(next);
    const result = fromDraft(next, toolNames);
    if ('problem' in result) {
      setProblem(result.problem);
      onChange(null);
    } else {
      setProblem(null);
      onChange(result);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label>
            <FieldLabel title="Chat type" />
            <select value={draft.chatType} disabled={disabled} onChange={(event) => update({ chatType: event.target.value })} className={smallField}>
              <option value="">general</option>
              <option value="crisis">crisis</option>
              <option value="vent">vent</option>
              <option value="learn">learn</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input type="checkbox" checked={draft.isPro} disabled={disabled} onChange={(event) => update({ isPro: event.target.checked })} />
            Pro user
          </label>
          <label>
            <FieldLabel title="Member for (days)" />
            <input type="number" min={0} value={draft.memberForDays} disabled={disabled} onChange={(event) => update({ memberForDays: event.target.value })} className={`${smallField} w-28`} />
          </label>
          <label>
            <FieldLabel title="Quest day" />
            <input type="number" min={0} value={draft.questDay} disabled={disabled} onChange={(event) => update({ questDay: event.target.value })} className={`${smallField} w-24`} />
          </label>
          {draft.questDay !== '' && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input type="checkbox" checked={draft.questDayCompleted} disabled={disabled} onChange={(event) => update({ questDayCompleted: event.target.checked })} />
              Day completed
            </label>
          )}
        </div>

        <ConversationEditor lines={draft.lines} hint="Ends with the user's message: the reply under test answers it." disabled={disabled} onChange={(lines) => update({ lines })} />

        <label className="block">
          <FieldLabel title="Context" hint="Optional JSON: ocdProfile, chatMemory, chatReference, as the app sends them" />
          <textarea
            value={draft.context}
            disabled={disabled}
            rows={draft.context ? 8 : 2}
            onChange={(event) => update({ context: event.target.value })}
            placeholder='{ "chatMemory": [{ "title": "Stove checking", "body": "..." }] }'
            className={`${fieldClass} font-mono text-xs`}
          />
        </label>
        {problem && <p className="text-sm text-red-700">{problem}</p>}
      </div>

      <div className="space-y-4">
        <p className="text-xs text-gray-600">One behaviour per line: the judge fails a line when any part of it is missing.</p>
        <ListField title="Must do" hint="What the reply must do" value={draft.mustDo} disabled={disabled} onChange={(mustDo) => update({ mustDo })} />
        <ListField title="Must not do" hint="Optional" value={draft.mustNotDo} disabled={disabled} onChange={(mustNotDo) => update({ mustNotDo })} />
        <div>
          <FieldLabel title="Tools" hint="Checked in code" />
          <div className="space-y-1">
            {toolNames.map((name) => (
              <label key={name} className="flex items-center justify-between gap-2 text-sm">
                <span className="font-mono text-gray-900">{name}</span>
                <select
                  value={draft.tools[name]}
                  disabled={disabled}
                  onChange={(event) => update({ tools: { ...draft.tools, [name]: TOOL_RULES.find((rule) => rule === event.target.value) ?? 'any' } })}
                  className={smallField}
                >
                  <option value="any">may call</option>
                  <option value="required">must call</option>
                  <option value="forbidden">must not call</option>
                </select>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
